/**
 * Canonical tab bar — HMS Light segmented/pill style.
 * Use this for ALL tab navigation in the app (page-level, nested filters, dialogs).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type TabDef = {
  key: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  count?: number | null;
  disabled?: boolean;
  className?: string;
};

export type TabBarProps = {
  tabs: TabDef[];
  activeTab: string;
  onTabChange: (key: string) => void;
  /**
   * Inset the pill strip (default true). Set false when the parent already
   * provides padding (e.g. a dialog body).
   */
  bordered?: boolean;
  size?: 'default' | 'sm';
  /** Stretch each tab equally — mobile split views. */
  equal?: boolean;
  className?: string;
  children?: ReactNode;
};

export function TabBar({
  tabs,
  activeTab,
  onTabChange,
  bordered = true,
  size = 'default',
  equal = false,
  className,
}: TabBarProps) {
  const navRef = useRef<HTMLElement>(null);
  const [fade, setFade] = useState({ left: false, right: false });

  const updateFade = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setFade((f) => (f.left === left && f.right === right ? f : { left, right }));
  }, []);

  // Single scrollable row: keep the active tab in view.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const active = el.querySelector<HTMLElement>('[aria-selected="true"]');
    if (active) {
      const pad = 32;
      const a = active.offsetLeft;
      const b = a + active.offsetWidth;
      if (a - pad < el.scrollLeft) el.scrollLeft = Math.max(0, a - pad);
      else if (b + pad > el.scrollLeft + el.clientWidth) el.scrollLeft = b + pad - el.clientWidth;
    }
    updateFade();
  }, [activeTab, tabs.length, updateFade]);

  useEffect(() => {
    const el = navRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(updateFade);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateFade]);

  return (
    <div className={cn('min-w-0 max-w-full', bordered && 'px-3 pt-3', className)}>
      <nav
        ref={navRef}
        role="tablist"
        onScroll={updateFade}
        data-fade-left={fade.left}
        data-fade-right={fade.right}
        className={cn(
          'scroll-fade-x scrollbar-none relative flex w-full max-w-full flex-nowrap items-center justify-start gap-1 overflow-x-auto overflow-y-hidden',
          equal && 'justify-stretch',
        )}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              disabled={tab.disabled}
              onClick={() => onTabChange(tab.key)}
              className={cn(
                'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg font-medium text-muted-foreground transition-all',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'disabled:pointer-events-none disabled:opacity-50',
                'hover:text-foreground hover:bg-foreground/[0.04]',
                size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3.5 py-1.5 text-sm',
                equal && 'flex-1',
                isActive && 'bg-primary/10 text-primary-text ring-1 ring-inset ring-primary/20 hover:bg-primary/10',
                tab.className,
              )}
            >
              {Icon && <Icon className={size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />}
              {tab.label}
              {tab.count != null && tab.count > 0 && (
                <span className="rounded-full bg-warning/15 px-1.5 py-0.5 text-[10px] font-semibold text-warning-text">
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
